/* eslint-disable jsx-a11y/anchor-is-valid */
import { parse, isWithinInterval } from "date-fns";
import PropTypes from "prop-types";
import React, { useCallback, useReducer, useState, useEffect, useRef } from "react";
import { useIntl } from "react-intl";
import {
	Button,
	Checkbox,
	Divider,
	Grid,
	Icon,
	List,
	Message,
	Modal,
	Pagination,
	Popup,
	Segment,
	Select,
	Table,
} from "semantic-ui-react";
import uuid from "uuid/v4";
import { useHistory } from "react-router-dom";
import { autoSortFn, checkIfWmx, downloadBlob, isNil } from "../../../libs/common_utils";
import { onBannerDismiss } from "../../../libs/component_utils";
import Registry from "../../../libs/register_storage";
import Pager from "../../../models/pager";
import AgenciesService from "../../../services/agencies";
import CampaignsService from "../../../services/campaign";
import ReportService from "../../../services/reports";
import ChartDiv from "../../common/chart";
import GridEmptyRow from "../../common/gridEmptyRow";
import { CampaignGridItem } from "../common/campaign_grid_item";
import { graph_metrics } from "../fixtures";
import CampaignGridContext from "./context";
import FilterControl from "./filter_control";
import Filter from "./models/filter";
import { campaignsActions as cA, campaignsGridReducer } from "./reducers";
import { CampaignsBulkEditModal } from "../edit/campaigns_bulk_edit_modal";
import "../index.css";
import { useFlags } from "launchdarkly-react-client-sdk";
import RetailAttributionReportModal from "../common/RetailAttributionReportModal";
import BodegaAudienceNotificationModal from "../common/BodegaAudienceNotificationModal";

const initialState = {
	campaigns: [],
	pager: new Pager(),
	filter: new Filter(),
};

const default_metrics = {
	METRIC_1: "total_spend",
	METRIC_2: "impressions",
};
const storage = new Registry(window.sessionStorage);
const winLocalStorage = new Registry(window.localStorage);

const RETAIL_ATTRIBUTION_NOTIFICATION_DISMISSED_KEY = "retail_attribution_notification_dismissed";
const RETAIL_ATTRIBUTION_NOTIFICATION_START = new Date(2026, 6, 15); // July 15, 2026
const RETAIL_ATTRIBUTION_NOTIFICATION_END = new Date(2026, 6, 22, 23, 59, 59); // July 22, 2026

const BODEGA_NOTIFICATION_DISMISSED_KEY = "bodega_audience_notification_dismissed";
const BODEGA_NOTIFICATION_START = new Date(2026, 8, 17); // Sept 17, 2026
const BODEGA_NOTIFICATION_END = new Date(2026, 8, 24, 23, 59, 59); // Sept 24, 2026

const CampaignsPage = ({ history }) => {
	const [state, dispatch] = useReducer(campaignsGridReducer, initialState);
	const [sortBy, setSortBy] = useState(null);
	const [sortDir, setSortDir] = useState('asc');
	const { whitelabelOfflineAttribution, whitelabelRetailAttribution } = useFlags();

	// full dataset cache for current filter
	const allRowsRef = useRef([]);

	const [graph, setGraph] = React.useState({
		data: [],
		loading: false,
	});
	const [graph_filter, setGraphFilter] = React.useState({
		metric_1: default_metrics.METRIC_1,
		metric_2: default_metrics.METRIC_2,
	});
	const [gridLoading, setGridLoading] = useState(false);
	const showSuccessMessage = !isNil(history.location.state);
	const [performanceGridData, setPerformanceGridData] = useState(
		storage.getItem("campaigns_performance_grid")
	);
	const [spendGridData, setSpendGridData] = useState(
		storage.getItem("campaigns_spend_grid")
	);

	const [campaignSelected, setCampaignSelected] = React.useState({});
	const [campaignForDuplication, setCampaignForDuplication] =
		React.useState(null);
	const [duplicatedCampaigns, setDuplicatedCampaigns] = React.useState([]);
	const [duplicatedCampaignsFailures, setDuplicatedCampaignsFailures] =
		React.useState([]);
  const [selectedCampaigns, setSelectedCampaigns] = useState([]);
  const [isBulkEditModalOpen, setIsBulkEditModalOpen] = useState(false);
  const [bulkEditResponseMessage, setBulkEditResponseMessage] = useState({successMessage: [], errorMessage: []});
  const [showBulkEditResponseMessage, setShowBulkEditResponseMessage] = useState(false);
	const [isDownloading, setIsDownloading] = useState(false);

	const [isRetailAttributionModalOpen, setIsRetailAttributionModalOpen] = useState(() =>
		whitelabelRetailAttribution && !winLocalStorage.getItem(RETAIL_ATTRIBUTION_NOTIFICATION_DISMISSED_KEY) && isWithinInterval(new Date(), {
			start: RETAIL_ATTRIBUTION_NOTIFICATION_START,
			end: RETAIL_ATTRIBUTION_NOTIFICATION_END,
		})
	);
	const [isBodegaModalOpen, setIsBodegaModalOpen] = useState(() => checkIfWmx() &&
		!winLocalStorage.getItem(BODEGA_NOTIFICATION_DISMISSED_KEY) &&
		isWithinInterval(new Date(), {
			start: BODEGA_NOTIFICATION_START,
			end: BODEGA_NOTIFICATION_END,
		})
	);
	const no_campaigns_initially = React.useRef(true);
	const timestamp = React.useRef(0);
	const services = React.useRef(
		new Map([
			["campaigns", new CampaignsService()],
			["agencies", new AgenciesService()],
			["report", new ReportService()],
		])
	);
	const intl = useIntl();

	const isPerformanceView = state.filter.view_by === "performance";
	const selectedGrid = isPerformanceView ? performanceGridData: spendGridData;

	const agency = services.current.get("agencies").getSelectedAgency() || 0;
	const isCampaignSelected = () => Object.keys(campaignSelected).length > 0;

  /**
   * only re-store performance column if flag is on
   */
  useEffect(() => {
    const defaultVisibleColumns = campaignPerformanceColumns(
      whitelabelOfflineAttribution
    );
    let currentStoredColumns =
      storage.getItem("campaigns_performance_grid") || {};

    if (!whitelabelOfflineAttribution) {
      const keysToRemove = Object.keys(offlineAttributionColumns);
      currentStoredColumns = Object.fromEntries(
        Object.entries(currentStoredColumns).filter(
          ([key]) => !keysToRemove.includes(key)
        )
      );
    }

    const resolvedColumns = {
      ...defaultVisibleColumns,
      ...currentStoredColumns,
    };
    storage.setItem("campaigns_performance_grid", resolvedColumns);
    setPerformanceGridData(resolvedColumns);
  }, [whitelabelOfflineAttribution]);

	/**
	 * Storing grid information data
	 */
	React.useEffect(() => {
		storage.setItem("campaigns_performance_grid", performanceGridData);
		storage.setItem("campaigns_spend_grid", spendGridData);
	}, [performanceGridData, spendGridData]);

	/**
	 * initial load campaigns
	 */
	React.useEffect(() => {
		state.filter.reset();
		state.pager.reset();

		const params = new URLSearchParams(history.location.search || "");
		if (params.toString()) {
			// hydrate filter from URL
			state.filter.fromQueryParams(params);
		}
		(async () => {
			await getCampaigns(state.filter, state.pager, true);
		})();
	}, [agency]);

	/**
	 * get graph data for campaign being selected
	 */
	React.useEffect(() => {
		if (!campaignSelected.id) {
			return undefined;
		}

		window.scrollTo(0, 0);
		let active = true;
		(async () => {
			try {
				setGraph({
					data: [],
					loading: true,
				});

				const params = Object.assign(
					{
						id: campaignSelected.id,
						m1: graph_filter.metric_1,
						m2: graph_filter.metric_2,
					},
					state.filter.toGraphJson()
				);
				const r = await services.current.get("report").campaignGraph(params);
				if (!active) return;
				let data2display = r.data.graph_data.map((x) => ({
					...x,
					date: parse(x.date, "yyyy-MM-dd", new Date()).valueOf(),
				}));

				data2display = data2display.sort((a, b) => a.date - b.date);
				timestamp.current = Date.now();
				setGraph({
					...graph,
					data: data2display,
					loading: false,
				});
			} catch (e) {
				if (!active) return;
				console.error(e);
				setGraph({
					...graph,
					loading: false,
				});
			}
		})();
		return () => { active = false; };
	}, [campaignSelected, graph_filter, state.filter.start_date]);

	const sortCampaigns = useCallback((sort_by, order) => {
		console.log(sort_by, order);
	}, []);

	/**
	 * get page
	 * @param e
	 * @param activePage
	 */
	const getPage = (e, { activePage }) => {
		state.pager.setPage(activePage);

		if (sortBy) {
			// client-side pagination on the sorted, full list
			applySortAndShowPage(activePage, sortBy, sortDir);
			return;
		}

		// server-side pagination if no sortBy
		const params = { ...state.pager.toJson(), ...state.filter.toRequestJson?.() };
		const campaignService = services.current.get('campaigns');

		(async () => {
			try {
				setGridLoading(true);
				const response = await campaignService.list(agency, params);
				dispatch({ type: cA.INIT, data: response.data, pager: response.meta });
			} catch (error) {
				console.error(error);
			} finally {
				setGridLoading(false);
			}
		})();
	};

	/**
	 * open chart
	 */
	const openChartAccordion = () => {
		document.getElementById("chart_swap_id").setAttribute("open", "true");
	};

	/**
	 * load campaigns from API
	 * @param {object} filter
	 * @param {object} pager
	 * @param {boolean} initial_load
	 * @return {Promise<void>}
	 */
	const getCampaigns = async (filter, pager, initial_load = false) => {
		const campaigns = services.current.get("campaigns");
		try {
			setGridLoading(true);

			let params = pager.toJsonNoLimit();
			if (filter) {
				params = Object.assign(params, filter.toRequestJson());
			}

			const r = await campaigns.list(agency, params);
			let meta = r.meta;
			meta.page = pager.page;
			allRowsRef.current = r.data;
			if (initial_load) {
				no_campaigns_initially.current = !Boolean(r.data.length);
				if (Boolean(r.data.length)) {
					setCampaignSelected(r.data[0]);
				}
			}
			dispatch({ type: cA.INIT, data: r.data.slice(0, pager.limit), pager: meta });
		} catch (e) {
			// ignore error
			console.error(e);
		} finally {
			setGridLoading(false);
		}
	};

	const duplicateCampaign = async (item) => {
		const campaigns = services.current.get("campaigns");
		campaigns
			.duplicate(item["id"])
			.then(() => {
				setDuplicatedCampaigns([...duplicatedCampaigns, item]);
				getCampaigns(state.filter, state.pager);
			})
			.catch((e) => {
				setDuplicatedCampaignsFailures([...duplicatedCampaigns, { ...item, errorMessage: e.error?.message ? e.error?.message : "" }]);
			});
	};

	/**
	 * filter campaigns
	 * @param {object} json
	 */
	const filterCampaigns = (json) => {
		state.pager.reset();
		state.filter.fromJson(json);

		setSortBy(null);
		setSortDir('asc');
		allRowsRef.current = [];

		// include current viewBy from grid
		const filterQuery = new URLSearchParams(state.filter.toQueryString());

		history.push({
			pathname: history.location.pathname,
			search: `?${filterQuery.toString()}`,
		})

		// server paginated fetch
		const allParams = { ...state.pager.toJson(), ...state.filter.toRequestJson?.() };
		const campaignService = services.current.get('campaigns');
		let params
		if (state.filter.isPerformanceView()) {
			// Remove time_window from params for performance view
			const {time_window, ...rest} = allParams;
			params = rest;
		} else {
			// Remove start_date and end_date from params for spend view
			const {start_date, end_date, ...rest} = allParams;
			params = rest;
		}

		(async () => {
			try {
				setGridLoading(true);
				const response = await campaignService.list(agency, params);
				dispatch({ type: cA.INIT, data: response.data, pager: response.meta });
			} catch (error) {
				console.error(error);
			} finally {
				setGridLoading(false);
			}
		})();
	};

	/**
	 * navigate user to create campaign page
	 */
	const navigateToCreatePage = useCallback(() => {
		history.push("/campaign/create");
	}, []);

	/**
	 * navigate user to edit page
	 */
	const navigateToEditPage = useCallback(
		(id) => {
			history.push(
				`/campaign/edit/${id}`,
				state.campaigns.find((campaign) => id === campaign.id)
			);
		},
		[state]
	);

	/**
	 * get edit page URL by campaign id
	 */
	const getEditPageHref = useCallback((id) => {
		return `./campaign/edit/${id}`;
	}, []);

	/**
	 * navigate user to create campaign page
	 */
	const navigateToStrategiesPage = useCallback((campaign_id) => {
		history.push(`/campaign/${campaign_id}/strategies`);
	}, []);

	/**
	 * Update filter metrics
	 * @param e
	 * @param {string} name
	 * @param {string} value
	 */
	const onChangeMetric = (e, { name, value }) => {
		setGraphFilter({
			...graph_filter,
			[name]: value,
		});
	};

  const handleBannerDismiss = useCallback(() => {
    setBulkEditResponseMessage({ successMessage: [], errorMessage: [] });
  }, [setBulkEditResponseMessage]);

  useEffect(() => {
    setShowBulkEditResponseMessage(bulkEditResponseMessage['successMessage'].length > 0 || bulkEditResponseMessage['errorMessage'].length > 0);
    const timer = setTimeout(handleBannerDismiss, 10000);
    return () => clearTimeout(timer);
  }, [bulkEditResponseMessage]);

	const applySortAndShowPage = (page = 1, columnKey, direction) => {
		const list = allRowsRef.current.slice().sort(autoSortFn(columnKey, direction));
		const perPage = state.pager.limit || 20;
		const start = (page - 1) * perPage;
		const data = list.slice(start, start + perPage);

		dispatch({ type: cA.UPDATE, data });
	};


	const onHeaderSort = async (columnKey) => {
		const nextDir = columnKey === sortBy ? (sortDir === 'asc' ? 'desc' : 'asc') : 'asc';

		// turn page to 1 for new sort
		state.pager.setPage(1);

		// fetch-all once for current filter if not cached
		if (!allRowsRef.current.length) {
			const allParams = { ...state.pager.toJsonNoLimit(), ...state.filter.toRequestJson?.() };
			let params;
			if (state.filter.isPerformanceView()) {
				// Remove time_window from params for performance view
				const {time_window, ...rest} = allParams;
				params = rest;
			} else {
				// Remove start_date and end_date from params for spend view
				const {start_date, end_date, ...rest} = allParams;
				params = rest;
			}

			const svc = services.current.get('campaigns');
			const resp = await svc.list(agency, params);
			allRowsRef.current = resp.data || [];
		}

		// now paginate client-side on the full list
		applySortAndShowPage(1, columnKey, nextDir);

		// update state AFTER applying (for header arrow + future clicks)
		setSortBy(columnKey);
		setSortDir(nextDir);
	};

	const handleCampaignDownload = async() => {
		const activeColumns = Object.keys(selectedGrid).filter((key) => selectedGrid[key]);
		try {
			setIsDownloading(true);
			const response = await services.current.get('campaigns').download(agency, {
				output: "csv",
				columns: activeColumns.join(","),
				...state.filter.toRequestJson?.(),
			});
			const blob = new Blob([response], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

			downloadBlob(blob, "campaigns.xlsx");
		} catch (e) {
			console.error(e);
		} finally {
			setIsDownloading(false);
		}
	}


	return (
		<Segment loading={gridLoading} basic style={{ padding: "0" }}>
			<CampaignGridContext.Provider
				value={{
					sorting: sortCampaigns,
					getPage: getPage,
					filtering: filterCampaigns,
					navigateToStrategiesPage,
					navigateToCreatePage,
					navigateToEditPage,
					getEditPageHref,
					setCampaignSelected,
					openChartAccordion,
					campaignForDuplication,
					setCampaignForDuplication,
					duplicateCampaign,
          selectedCampaigns,
          setSelectedCampaigns,
          isBulkEditModalOpen,
          setIsBulkEditModalOpen,
          dispatch,
          setBulkEditResponseMessage,
					onHeaderSort,
					sortBy,
					sortDir,
					setPerformanceGridData,
					setSpendGridData,
				}}
			>
				{showSuccessMessage && (
					<CampaignsSuccessMessage details={history.location.state || {}} />
				)}
        {showBulkEditResponseMessage &&
          <>
            <Message
                success
                hidden={bulkEditResponseMessage['successMessage'].length === 0}
                onDismiss={handleBannerDismiss}>
                  <div>
                    {intl.formatMessage({
                      id: "MESSAGE_CAMPAIGN_BULK_EDIT_UPDATED",
                      defaultMessage: "Campaigns successfully updated"
                    })}
                    <ul>
                      {bulkEditResponseMessage['successMessage'].map((campaign) => (
                        <li key={campaign.id}>{campaign.name}</li>
                      ))}
                    </ul>
                  </div>
              </Message>
              <Message
                error
                hidden={bulkEditResponseMessage['errorMessage'].length === 0}
                onDismiss={handleBannerDismiss}>
                <div>
                  {intl.formatMessage({
                    id: "MESSAGE_CAMPAIGN_BULK_EDIT_NOT_UPDATED",
                    defaultMessage: "These campaigns were not updated"
                  })}
                  <ul>
                    {bulkEditResponseMessage['errorMessage'].map((campaign) => (
                      <li key={uuid()}>{campaign.message}</li>
                    ))}
                  </ul>
                </div>
              </Message>
          </>
        }
				{duplicatedCampaigns.length > 0 && (
					<Message success onDismiss={() => setDuplicatedCampaigns([])}>
						{intl.formatMessage(
							{
								id: "SUCCESSFUL_DUPLICATION_MSG",
								defaultMessage: `Successfully duplicated: ${duplicatedCampaigns
									.map((item) => item.title)
									.join(", ")}`,
							},
							{
								title: duplicatedCampaigns.map((item) => item.title).join(", "),
							}
						)}
					</Message>
				)}
				{duplicatedCampaignsFailures.length > 0 && (
					<Message
						negative
						onDismiss={() => setDuplicatedCampaignsFailures([])}
					>
						{intl.formatMessage(
							{
								id: "FAILED_DUPLICATION_MSG",
								defaultMessage: `Failed duplicating: ${duplicatedCampaignsFailures
									.map((item) => item.title)
									.join(", ")}`,
							},
							{
								title: duplicatedCampaignsFailures
									.map((item) => item.title)
									.join(", "),
							}
						)}
						{duplicatedCampaignsFailures.map((item, index) => <span key={`message-${index}`} className="error-message-child">{`${intl.formatMessage(
							{
								id: "ERROR",
								defaultMessage: `Error`,
							}
						)}: ${item.errorMessage}`}</span>)}
					</Message>
				)}
				<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center"}}>
					<h1>
						{intl.formatMessage({
							id: "HEADING_CAMPAIGNS",
							defaultMessage: "Campaigns",
						})}
					</h1>
					<Button compact type="button" onClick={handleCampaignDownload} loading={isDownloading}>
						{intl.formatMessage({
							id: "BTN_DOWNLOAD_CAMPAIGNS",
							defaultMessage: "Download Campaigns",
						})}
					</Button>
				</div>
				
				{isCampaignSelected() && (
					<details id="chart_swap_id">
						<summary>
							<hr />{" "}
							<div>
								{intl.formatMessage({
									id: "BUTTON_SHOW_HIDE_CHART",
									defaultMessage: "Show / hide chart",
								})}
							</div>
						</summary>
						<Grid>
							<Grid.Column verticalAlign="middle" style={{ width: "auto" }}>
								<strong>{campaignSelected.title}</strong>
							</Grid.Column>
							<Grid.Column width={2}>
								<Select
									selection
									fluid
									options={graph_metrics(intl).filter(
										(x) => x.value !== graph_filter.metric_2
									)}
									name="metric_1"
									disabled={graph.loading}
									onChange={onChangeMetric}
									value={graph_filter.metric_1}
								/>
							</Grid.Column>
							<Grid.Column
								style={{ width: "15px" }}
								verticalAlign="middle"
								textAlign="center"
							>
								{intl.formatMessage({
									id: "LABEL_CAMPAIGN_VS_METRIC",
									defaultMessage: "vs",
								})}
							</Grid.Column>
							<Grid.Column width={2}>
								<Select
									selection
									fluid
									options={graph_metrics(intl).filter(
										(x) => x.value !== graph_filter.metric_1
									)}
									name="metric_2"
									disabled={graph.loading}
									onChange={onChangeMetric}
									value={graph_filter.metric_2}
								/>
							</Grid.Column>
						</Grid>
						{graph.loading ? (
							<Segment loading />
						) : (
							<ChartDiv
								updated={timestamp.current}
								filter={graph_filter}
								data={graph.data}
							/>
						)}
					</details>
				)}
				<Divider hidden />
				<CampaignsGrid
					items={state.campaigns}
					loading={gridLoading}
					advertisers_disabled={no_campaigns_initially.current}
					controls={{ pager: state.pager, filter: state.filter }}
					selectedGrid={selectedGrid}
				/>
			</CampaignGridContext.Provider>

			<RetailAttributionReportModal
				isOpen={isRetailAttributionModalOpen}
				onClose={(dontShowAgain) => {
					if (dontShowAgain) {
						winLocalStorage.setItem(RETAIL_ATTRIBUTION_NOTIFICATION_DISMISSED_KEY, true);
					}
					setIsRetailAttributionModalOpen(false);
				}}
				onPrimaryClick={(dontShowAgain) => {
					if (dontShowAgain) {
						winLocalStorage.setItem(RETAIL_ATTRIBUTION_NOTIFICATION_DISMISSED_KEY, true);
					}
					setIsRetailAttributionModalOpen(false);
					history.push("/report/retail-attribution");
				}}
			/>
			<BodegaAudienceNotificationModal
				isOpen={isBodegaModalOpen}
				onClose={(dontShowAgain) => {
					if (dontShowAgain) {
						winLocalStorage.setItem(BODEGA_NOTIFICATION_DISMISSED_KEY, true);
					}
					setIsBodegaModalOpen(false);
				}}
			/>
		</Segment>
	);
};

/**
 * Generate success message
 * @param {object} details
 * @return {*}
 * @constructor
 */
const CampaignsSuccessMessage = ({ details }) => {
	const intl = useIntl();
	React.useEffect(() => {
		const timer = setTimeout(onBannerDismiss, 25000);
		return () => {
			clearTimeout(timer);
		};
	}, []);

	if (details.action === "created") {
		return (
			<Message
				success
				className="page-success-message"
				attached
				onDismiss={onBannerDismiss}
			>
				{intl.formatMessage({
					id: "MESSAGE_CAMPAIGN_CREATED",
					defaultMessage: "Campaign successfully created",
				})}
			</Message>
		);
	} else if (details.action === "updated") {
		return (
			<Message
				success
				className="page-success-message"
				attached
				onDismiss={onBannerDismiss}
			>
				{intl.formatMessage(
					{
						id: "MESSAGE_CAMPAIGN_UPDATED",
						defaultMessage: "Campaign {name} updated",
					},
					{
						name: details.name,
					}
				)}
			</Message>
		);
	}

	return <></>;
};
CampaignsSuccessMessage.propTypes = {
	details: PropTypes.object.isRequired,
};

const offlineAttributionColumns = {
	online_conversions: true,
	online_revenue: true,
	online_roi: true,
	offline_conversions: true,
	offline_revenue: true,
	offline_roi: true,
};

const campaignPerformanceColumns = (whitelabelOfflineAttributionFlag = false) => {
  return {
    id: true,
    title: true,
    status: true,
    start_date: true,
    end_date: true,
    budget: true,
    impressions: true,
    clicks: true,
    total_conversions: true,
    total_revenue: true,
    spend_to_date: true,
    total_spend_cpa: false,
    total_spend_cpc: false,
    total_spend_cpm: false,
    ctr: false,
    video_complete: false,
		total_spend_roi: true,
		...(whitelabelOfflineAttributionFlag ? offlineAttributionColumns : {}),
  };
};

const campaignSpendColumns = {
	id: true,
	title: true,
	status: true,
	start_date: true,
	end_date: true,
	goal_type: true,
	flight_pacing: true,
	daily_pacing: true,
	budget: true,
	campaign_flight_pacing_remaining: true,
	total_spend_yesterday: true,
	campaign_frequency_type: true,
	spend_to_date: true,
}

const columnNames = new Map([
	["budget", "Budget"],
	["campaign_flight_pacing_remaining", "Budget Remaining"],
	["status", "Status"],
	["start_date", "Start Date"],
	["end_date", "End Date"],
	["campaign_spend", "Spend"],
	["campaign_ctr", "CTR"],
	["ctr", "CTR"],
	["clicks", "Clicks"],
	["campaign_clicks", "Clicks"],
	["id", "Campaign ID"],
	["campaign_impressions", "Impressions"],
	["title", "Campaign Name"],
	["daily_pacing", "Daily Pacing"],
	["campaign_frequency_type", "Frequency Cap"],
	["flight_pacing", "Flight Pacing"],
	["impressions", "Impressions"],
	["online_conversions", "Online Conversions"],
	["online_revenue", "Online Revenue"],
	["online_roi", "Online ROI"],
	["offline_conversions", "Offline Conversions"],
	["offline_revenue", "Offline Revenue"],
	["offline_roi", "Offline ROI"],
	["total_spend_yesterday", "Spend Yesterday"],
	["total_conversions", "Total Conversions"],
	["total_revenue", "Total Revenue"],
	["total_spend_roi", "Total ROI"],
	["spend_to_date", "Total Spend"],
	["total_spend_cpa", "Total Spend eCPA"],
	["total_spend_cpc", "Total Spend eCPC"],
	["total_spend_cpm", "Total Spend eCPM"],
	["video_complete", "100% Completed Rate"],
	["goal_type", "Goal Type"],
	["goal_value", "Goal Value"],
]);
const columnMessageIds = new Map([
	["budget", "LABEL_BUDGET"],
	["campaign_flight_pacing_remaining", "LABEL_BUDGET_REMAINING"],
	["status", "LABEL_STATUS"],
	["start_date", "LABEL_START_DATE"],
	["end_date", "LABEL_END_DATE"],
	["campaign_spend", "LABEL_SPEND"],
	["campaign_ctr", "LABEL_CTR"],
	["campaign_clicks", "LABEL_CLICKS"],
	["id", "LABEL_CAMPAIGN_ID"],
	["campaign_impressions", "LABEL_IMPRESSIONS"],
	["title", "LABEL_CAMPAIGN_NAME"],
	["clicks", "LABEL_CLICKS"],
	["ctr", "LABEL_CTR"],
	["daily_pacing", "LABEL_DAILY_PACING"],
	["campaign_frequency_type", "LABEL_FREQUENCY_CAP"],
	["flight_pacing", "LABEL_FLIGHT_PACING"],
	["impressions", "LABEL_IMPRESSIONS"],
	["online_conversions", "LABEL_ONLINE_CONVERSIONS"],
	["online_revenue", "LABEL_ONLINE_REVENUE"],
	["online_roi", "LABEL_ONLINE_ROI"],
	["offline_conversions", "LABEL_OFFLINE_CONVERSIONS"],
	["offline_revenue", "LABEL_OFFLINE_REVENUE"],
	["offline_roi", "LABEL_OFFLINE_ROI"],
	["total_spend_yesterday", "LABEL_SPEND_YESTERDAY"],
	["spend_to_date", "LABEL_TOTAL_SPEND"],
	["total_spend_roi", "LABEL_TOTAL_ROI"],
	["video_complete", "LABEL_VIDEO_COMPLETE_RATE"],
  ["total_conversions", "LABEL_TOTAL_CONVERSIONS"],
	["total_revenue", "LABEL_TOTAL_REVENUE"],
	["total_spend_cpa", "LABEL_TOTAL_SPEND_CPA"],
	["total_spend_cpc", "LABEL_TOTAL_SPEND_CPC"],
	["total_spend_cpm", "LABEL_TOTAL_SPEND_CPM"],
	["goal_type", "LABEL_GOAL_TYPE"],
	["goal_value", "LABEL_GOAL_VALUE"],
]);

function storeCampaignGridColumns() {
	// set storage for campaign columns information
	if (!storage.getItem("campaigns_performance_grid")) {
		storage.setItem("campaigns_performance_grid", campaignPerformanceColumns(false));
	}
	if (!storage.getItem("campaigns_spend_grid")) {
		storage.setItem("campaigns_spend_grid", campaignSpendColumns);
	}
}

// Initial column storage
storeCampaignGridColumns()

/**
 * Generate campaigns grid
 * @param {array} items
 * @param {object} control
 * @return {*}
 * @constructor
 */
const CampaignsGrid = ({
	items,
	loading,
	controls,
	selectedGrid,
	advertisers_disabled = false,
}) => {
	const intl = useIntl();
	const history = useHistory()
	const containerRef = useRef(null);
	const headerRef = useRef(null);
  const firstRowRef = useRef(null);
	const context = React.useContext(CampaignGridContext);
	const { onHeaderSort, sortBy, sortDir } = context

	/**
	 * check should we show a cell based on flag in the map
	 * @param cellName
	 * @return {string}
	 */
	const cellVisible = (cellName) => {
		return selectedGrid.hasOwnProperty(cellName) && selectedGrid[cellName]
			? ""
			: "invisible";
	};

	/**
	 * click on the button to hide a popup
	 */
	const listener = () => {
		document.getElementById("edit-column-button").click();
	};

	/**
	 * attach a listener for mouse leave on edit-column popup
	 */
	const attachListener = () => {
		document
			.getElementById("edit-column-id")
			.addEventListener("mouseleave", listener);
	};

	/**
	 * remove a listener for mouse leave on component unmount
	 */
	const removeListener = () => {
		document
			.getElementById("edit-column-id")
			.removeEventListener("mouseleave", listener);
	};

	const onPageChange = (e, { activePage }) => {
		if (firstRowRef.current) {
			firstRowRef.current.scrollIntoView({ block: "start", behavior: "auto" });

			// offset for sticky header height so the row isn't hidden under it
			const headerH = headerRef.current?.offsetHeight || 0;
			const container = containerRef.current;
			if (container) container.scrollTop -= (headerH - 50);
		}
    context.getPage(e, { activePage });
  };

	const {
		campaignForDuplication,
		setCampaignForDuplication,
		duplicateCampaign,
    selectedCampaigns,
    setSelectedCampaigns,
    isBulkEditModalOpen,
    setIsBulkEditModalOpen,
    dispatch,
    setBulkEditResponseMessage,
		setSpendGridData,
		setPerformanceGridData,
	} = context;

  const areAllCampaignsChecked = selectedCampaigns.length === items.length;

  const handleCheckboxChange = useCallback((e, { checked }) => {
    setSelectedCampaigns(checked ? items.map(({ id }) => id) : []);
  }, [items, setSelectedCampaigns]);
	const isPerformanceView = controls.filter.view_by === "performance";

	return (
		<>
			<Modal
				open={campaignForDuplication}
				onClose={() => setCampaignForDuplication(null)}
				closeOnDimmerClick={false}
				closeOnDocumentClick={false}
			>
				<Modal.Header>
					{intl.formatMessage({
						id: "DUPLICATION_CONFIRMATION_HEADER",
						defaultMessage: "Confirm duplication",
					})}
				</Modal.Header>
				<Modal.Content>
					{campaignForDuplication &&
						intl.formatMessage(
							{
								id: "DUPLICATION_CAMPAIGN_CONFIRMATION_MSG",
								defaultMessage:
									"Are you sure you want to duplicate campaign - {title}?",
							},
							{ title: campaignForDuplication["title"] }
						)}
				</Modal.Content>
				{context.campaignForDuplication && (
					<Modal.Actions>
						<Button onClick={() => setCampaignForDuplication(null)}>
							{intl.formatMessage({
								id: "BTN_CANCEL",
								defaultMessage: "Cancel",
							})}
						</Button>
						<Button
							primary
							onClick={() => {
								duplicateCampaign({ ...campaignForDuplication });
								setCampaignForDuplication(null);
							}}
						>
							{intl.formatMessage({
								id: "BTN_OK",
								defaultMessage: "Ok",
							})}
						</Button>
					</Modal.Actions>
				)}
			</Modal>
			<Grid className="common_grid" doubling stackable columns={2}>
					<Grid.Column width={16}>
						<FilterControl
							filter={controls.filter}
							advertisers_disabled={advertisers_disabled}
							onChange={context.filtering}
						/>
					</Grid.Column>
					<Grid.Column width={16}
						className=""
						textAlign="right"
						verticalAlign="middle"
					>
						<div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", flexWrap: "wrap", gap: "8px", rowGap: "8px" }}>
							<Popup
								trigger={
									<Button
										className="text__uppercase"
										id="edit-column-button"
										compact
                    data-testid="edit_columns_button"
									>
										{intl.formatMessage({
											id: "BTN_EDIT_COLUMNS",
											defaultMessage: "Edit Columns",
										})}
									</Button>
								}
								onMount={attachListener}
								onUnmount={removeListener}
								id="edit-column-id"
								content={
									<List>
										{Object.keys(selectedGrid).map((column) => {
											return (
												<List.Item key={uuid()}>
                          <Checkbox
                            label={intl.formatMessage({
                              id: columnMessageIds.get(column),
                              defaultMessage: columnNames.get(column),
                            })}
                            name={column}
                            defaultChecked={selectedGrid[column]}
                            onChange={(e, { name, checked }) => {
                              (isPerformanceView ? setPerformanceGridData : setSpendGridData)(prev => ({ ...prev, [name]: checked }));
                            }}
                            as={"label"}
                            id={`column_${column}_checkbox`}
                            data-testid={`column_${column}_checkbox`}
                          />
												</List.Item>
											);
										})}
									</List>
								}
								on="click"
								position="bottom right"
							/>
              <Popup
                content={
                  intl.formatMessage({
                    id: "TOOLTIP_SELECT_CAMPAIGNS_TO_BULK_EDIT",
                    defaultMessage: "Select one or more Campaigns to Bulk Edit"
                  })
                }
                size="tiny"
                inverted
                position="top center"
                disabled={selectedCampaigns.length > 0}
                trigger={
                  <div style={{ display: 'inline-block' }}>
                    <Button
                      className="text__uppercase"
                      primary
                      compact
                      onClick={() => setIsBulkEditModalOpen(true)}
                      disabled={selectedCampaigns.length <= 0}
                    >
                      {intl.formatMessage({
                        id: "BTN_EDIT_CAMPAIGNS",
                        defaultMessage: "Bulk Edit Campaigns",
                      })}
                    </Button>
                  </div>
                }
              />
							<Button
								className="text__uppercase"
								primary
								compact
								onClick={() => context.navigateToCreatePage()}
                data-testid="add_campaign_button"
							>
								{intl.formatMessage({
									id: "BTN_ADD_CAMPAIGN",
									defaultMessage: "Add Campaign",
								})}
							</Button>
						</div>
					</Grid.Column>
				
			</Grid>
      <div className="campaign-grid-table-container">
        <Table className="custom-table" sortable>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell style={{width:"40px"}}>
                <Checkbox
                  checked={areAllCampaignsChecked}
                  onChange={handleCheckboxChange}
                />
              </Table.HeaderCell>
							{Object.keys(selectedGrid).map((column) => {
								if (!selectedGrid[column]) return null;
								return (
									<Table.HeaderCell 
										key={column} 
										textAlign="left" 
										className={column === 'title' ? 'campaign-name-cell' : ''}
										onClick={() => onHeaderSort(column)}
										sorted={sortBy === column ? (sortDir === 'asc' ? 'ascending' : 'descending') : null}
										style={{ cursor: 'pointer' }}
									>
										{intl.formatMessage({
											id: columnMessageIds.get(column),
											defaultMessage: columnNames.get(column),
										})}
										{sortBy === column ? null : <Icon name="sort" size="small" />}
									</Table.HeaderCell>
								)
							})}
              <Table.HeaderCell textAlign="center" key="actions">&nbsp;</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
					<tbody ref={firstRowRef}></tbody>
          <Table.Body>
            {items.map((item, idx) => {
              return (
                <CampaignGridItem
                  key={item.id}
									item={item}
									selectedGrid={selectedGrid}
                  cellVisible={cellVisible}
                />
              );
            })}
            <GridEmptyRow
              length={items.length}
              loading={loading}
              handleAddButton={context.navigateToCreatePage}
              filterTouched={!controls.filter.isEmpty()}
              addButtonLabel={intl.formatMessage({
                id: "BTN_ADD_CAMPAIGN",
                defaultMessage: "Add Campaign",
              })}
              notFoundMessage={intl.formatMessage({
                id: "EMPTY_CAMPAIGNS",
                defaultMessage: "You don’t have any campaigns yet",
              })}
            />
          </Table.Body>
          <Table.Footer>
					  <Table.Row>
						  <Table.Cell className='campaign-grid-footer'>
							  {controls.pager.total_pages > 1 && (
                  <Pagination
                    size="mini"
                    activePage={controls.pager.page}
                    totalPages={controls.pager.total_pages}
                    firstItem={null}
                    lastItem={null}
                    onPageChange={onPageChange}
								  />
							  )}
						  </Table.Cell>
					</Table.Row>
				</Table.Footer>
        </Table>
        <CampaignsBulkEditModal
          isBulkEditModalOpen={isBulkEditModalOpen}
          setIsBulkEditModalOpen={setIsBulkEditModalOpen}
          selectedCampaigns={selectedCampaigns}
          items={items}
          dispatch={dispatch}
          setBulkEditResponseMessage={setBulkEditResponseMessage}
        />
      </div>
		</>
	);
};
CampaignsGrid.propTypes = {
	items: PropTypes.array.isRequired,
	controls: PropTypes.object.isRequired,
	loading: PropTypes.bool,
	selectedGrid: PropTypes.object.isRequired,
	advertisers_disabled: PropTypes.bool,
};

export default CampaignsPage;
